import type {StudioVersion} from '../../../data/api/StudioApi';
import {ApiError} from '../errors';
import type {JobView} from '../types';

/** The two requests `cancelJob` sends, as the job store sends them. */
export interface CancelRequests {
  getJob(organizationId: string | undefined, id: string): Promise<JobView>;
  cancel(
    organizationId: string | undefined,
    id: string,
    version: StudioVersion,
  ): Promise<unknown>;
}

/** The statuses in which Studio drafts or generates the job. */
export const ACTIVE = new Set<string>([
  'ingesting',
  'planning',
  'checking',
  'rendering',
]);

/** True while Studio drafts, generates or finishes the job. */
export const isWorking = (view: JobView) =>
  !!view.running || ACTIVE.has(view.job.status);

/**
 * True while Cancel can still stop the job. A done job is only saving its finished
 * microreels, and canceling then would mark them failed.
 */
export const canCancel = (view: JobView) =>
  isWorking(view) &&
  view.job.status !== 'done' &&
  !view.job.cancellationRequested;

/**
 * Stops the job's current work. Each stage the server reaches saves the job, so the version
 * the page holds is often stale by the time Cancel is clicked: on a conflict, read the job
 * again and try once more with its current version. It reads the job itself because the
 * page's reload gives way to the page's next poll and then returns nothing.
 */
export async function cancelJob(
  requests: CancelRequests,
  organizationId: string | undefined,
  id: string,
  storeVersion: StudioVersion,
) {
  try {
    await requests.cancel(organizationId, id, storeVersion);
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 409) throw error;
    const current = await requests.getJob(organizationId, id);
    if (!current?.job) throw error;
    // It finished, stopped or started canceling in the meantime: nothing is left to cancel.
    if (!canCancel(current)) return;
    await requests.cancel(organizationId, id, current.job.storeVersion);
  }
}

/*
 * The server's stages while a job waits rather than works: queued until a worker starts it
 * ("Waiting to start"), waiting for a render slot ("Waiting for a free render slot", maybe
 * with "(3 ahead)"), or handed back to continue later ("Resuming after a Studio restart",
 * "Paused for a Studio update. …", "Studio was busy. The render continues …").
 */
// Also waiting for the AI service: "Studio's AI service is at capacity right now. Your video
// will continue automatically."
const WAITING_STAGE =
  /^(?:Waiting\b|Resuming after\b|Paused for\b|Studio was busy\b|Studio['’]s AI service is at capacity\b)/i;
// Generating is done and the playable previews are ready: the MP4s are being made.
const EXPORTING_STAGE =
  /^(?:Exporting\b|Joining reels\b|Finishing the video\b)/i;

/** The stage the job waits in (see WAITING_STAGE), as the server says it; '' otherwise. */
export function waitingStage(job: {stage?: string | null}): string {
  const stage = job.stage?.trim() || '';
  return WAITING_STAGE.test(stage) ? stage : '';
}

/** True while Studio makes the job's MP4s, after its previews were made. */
export const isExporting = (job: {stage?: string | null}) =>
  EXPORTING_STAGE.test(job.stage?.trim() || '');
